import { PaymentItemSchema } from '@/schemas/PaymentItemSchema'
import { PaymentStatusSchema } from '@/schemas/PaymentStatusSchema'
import { z } from 'zod'

export interface PaginatedTransactions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[]
  total: number
  current_page: number
  per_page: number
}

export type ITransaction = {
  transaction_id: number
  payment_id: number
  status: number
  type: number
  transaction_type: string
  invoice_quote: string
  total_amount: number
  comments: string
  program_id: number
  scholarship_application_id: number | null
  student_name: string[]
  applicant_name: string
  provider_id: number
  provider_name: string
  created_datetime: string
  expense_categories: { id: number; name: string }[]
}

export const PurseMovementSchema = z.object({
  id: z.number(),
  purse_id: z.number(),
  total_amount: z.number(),
  name: z.string(),
  amount: z.number(),
  created_datetime: z.string().datetime(),
  last_datetime: z.string().datetime(),
  student_name: z.string(),
})
export type PurseMovementSchema = z.infer<typeof PurseMovementSchema>

const StudentSchema = z.object({
  student_id: z.number(),
  student_name: z.string(),
})

// Define the main transaction schema
const TransactionSchema = z.object({
  transaction_id: z.number(),
  payment_id: z.number(),
  status: PaymentStatusSchema,
  type: z.number(),
  total_amount: z.number(),
  service_fee: z.number(),
  comments: z.string().nullable().optional(),
  invoice_quote: z.string().nullable().optional(),
  amount_provider: z.number(),
  transaction_type: z.string(),
  program_id: z.number(),
  tracking_number: z.string().nullable(),
  payment_items: z.array(PaymentItemSchema).optional(),
  has_appealed: z.number().nullable(),
  last_message: z.string().nullable(),
  scholarship_application_id: z.null(),
  applicant_name: z.string(),
  provider_id: z.number().nullable(),
  provider_name: z.string().nullable(),
  created_datetime: z.string().datetime(),
  expense_categories: z.array(
    z.object({
      name: z.string(),
      id: z.number(),
      amount: z.number(),
    })
  ),
  payment_documents: z.array(
    z.object({
      path: z.string(),
    })
  ),
  purse_movements: z.array(PurseMovementSchema),
  students: z.array(StudentSchema),
})

export default TransactionSchema

const PaginationLinkSchema = z.object({
  url: z.string().nullable(),
  label: z.string(),
  active: z.boolean(),
})

export const AdminTransactionsSchema = z.object({
  current_page: z.number(),
  data: z.array(
    z.object({
      transaction_id: z.number(),
      payment_id: z.number(),
      status: z.number(),
      type: z.number(),
      transaction_type: z.string(),
      invoice_quote: z.string().nullable(),
      total_amount: z.number(),
      comments: z.string().nullable(),
      program_id: z.number(),
      scholarship_application_id: z.number().nullable(),
      student_name: z.array(z.string()),
      applicant_name: z.string(),
      provider_id: z.number(),
      provider_name: z.string(),
      amount_provider: z.number(),
      service_fee: z.number(),
      service_fee_payer: z.string(),
      created_datetime: z.string(),
      expense_categories: z
        .array(
          z.object({
            id: z.number(),
            name: z.string(),
            amount: z.number(),
          })
        )
        .optional(),
    })
  ),
  first_page_url: z.string(),
  from: z.number().nullable(),
  last_page: z.number(),
  last_page_url: z.string(),
  links: z.array(PaginationLinkSchema),
  next_page_url: z.string().nullable(),
  path: z.string(),
  per_page: z.number(),
  prev_page_url: z.string().nullable(),
  to: z.number().nullable(),
  total: z.number(),
})
export type AdminTransactionsSchemaType = z.infer<
  typeof AdminTransactionsSchema
>
